import Dexie from 'dexie';

export class BabySleepDB extends Dexie {
  constructor() {
    super('BabySleepDB');
    
    this.version(4).stores({
      children: '++id, name, birthDate, createdAt, profileImage, theme, timezone',
      sleepEntries: '++id, childId, date, startTime, endTime, type, comment, specialEvent, timezone, createdAt',
      sleepPatterns: '++id, childId, patternType, averageTime, confidence, timezone, lastUpdated',
      timezoneHistory: '++id, childId, timezone, timestamp, changeInfo'
    });

    // Add error handling for database operations
    this.on('blocked', () => {
      console.error('Database blocked - version upgrade needed');
    });

    this.on('versionchange', (event) => {
      console.log('Database version changed:', event);
    });
  }

  // Test database connection
  async testConnection() {
    try {
      await this.open();
      console.log('Database connection successful');
      return true;
    } catch (error) {
      console.error('Database connection failed:', error);
      return false;
    }
  }
}

export const db = new BabySleepDB();

// LocalStorage backup/restore functions
const STORAGE_KEY = 'babySleepTracker_backup';
const BACKUP_INTERVAL = 5 * 60 * 1000; // 5 minutes

export const localStorageBackup = {
  // Create backup of all data
  async createBackup() {
    try {
      const children = await db.children.toArray();
      const sleepEntries = await db.sleepEntries.toArray();
      const sleepPatterns = await db.sleepPatterns.toArray();
      const timezoneHistory = await db.timezoneHistory.toArray();
      
      const backup = {
        version: 4,
        timestamp: new Date().toISOString(),
        children,
        sleepEntries,
        sleepPatterns,
        timezoneHistory
      };
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(backup));
      console.log('Backup created successfully');
      return true;
    } catch (error) {
      console.error('Error creating backup:', error);
      return false;
    }
  },

  // Restore data from backup
  async restoreBackup() {
    try {
      const backupData = localStorage.getItem(STORAGE_KEY);
      if (!backupData) {
        console.log('No backup found');
        return false;
      }

      const backup = JSON.parse(backupData);
      
      // Clear existing data
      await db.children.clear();
      await db.sleepEntries.clear();
      await db.sleepPatterns.clear();
      await db.timezoneHistory.clear();
      
      // Restore data with better error handling
      if (backup.children && backup.children.length > 0) {
        try {
          await db.children.bulkAdd(backup.children);
        } catch (error) {
          console.warn('Error bulk adding children, trying individual adds:', error);
          // Fallback to individual adds
          for (const child of backup.children) {
            try {
              await db.children.add(child);
            } catch (addError) {
              console.warn('Error adding individual child:', addError);
            }
          }
        }
      }
      
      if (backup.sleepEntries && backup.sleepEntries.length > 0) {
        try {
          await db.sleepEntries.bulkAdd(backup.sleepEntries);
        } catch (error) {
          console.warn('Error bulk adding sleep entries, trying individual adds:', error);
          // Fallback to individual adds
          for (const entry of backup.sleepEntries) {
            try {
              await db.sleepEntries.add(entry);
            } catch (addError) {
              console.warn('Error adding individual sleep entry:', addError);
            }
          }
        }
      }

      if (backup.sleepPatterns && backup.sleepPatterns.length > 0) {
        try {
          await db.sleepPatterns.bulkAdd(backup.sleepPatterns);
        } catch (error) {
          console.warn('Error bulk adding sleep patterns, trying individual adds:', error);
          // Fallback to individual adds
          for (const pattern of backup.sleepPatterns) {
            try {
              await db.sleepPatterns.add(pattern);
            } catch (addError) {
              console.warn('Error adding individual sleep pattern:', addError);
            }
          }
        }
      }

      if (backup.timezoneHistory && backup.timezoneHistory.length > 0) {
        try {
          await db.timezoneHistory.bulkAdd(backup.timezoneHistory);
        } catch (error) {
          console.warn('Error bulk adding timezone history, trying individual adds:', error);
          // Fallback to individual adds
          for (const timezoneEntry of backup.timezoneHistory) {
            try {
              await db.timezoneHistory.add(timezoneEntry);
            } catch (addError) {
              console.warn('Error adding individual timezone entry:', addError);
            }
          }
        }
      }
      
      console.log('Backup restored successfully');
      return true;
    } catch (error) {
      console.error('Error restoring backup:', error);
      return false;
    }
  },

  // Export data as JSON
  async exportData() {
    try {
      const children = await db.children.toArray();
      const sleepEntries = await db.sleepEntries.toArray();
      const sleepPatterns = await db.sleepPatterns.toArray();
      const timezoneHistory = await db.timezoneHistory.toArray();
      
      const exportData = {
        version: 4,
        exportDate: new Date().toISOString(),
        children,
        sleepEntries,
        sleepPatterns,
        timezoneHistory
      };
      
      return exportData;
    } catch (error) {
      console.error('Error exporting data:', error);
      throw error;
    }
  },

  // Import data from JSON
  async importData(importData) {
    try {
      // Validate import data
      if (!importData.children || !importData.sleepEntries) {
        throw new Error('Invalid import data format');
      }

      // Clear existing data
      await db.children.clear();
      await db.sleepEntries.clear();
      await db.sleepPatterns.clear();
      await db.timezoneHistory.clear();
      
      // Import new data with better error handling
      if (importData.children.length > 0) {
        try {
          await db.children.bulkAdd(importData.children);
        } catch (error) {
          console.warn('Error bulk adding children during import, trying individual adds:', error);
          // Fallback to individual adds
          for (const child of importData.children) {
            try {
              await db.children.add(child);
            } catch (addError) {
              console.warn('Error adding individual child during import:', addError);
            }
          }
        }
      }
      
      if (importData.sleepEntries.length > 0) {
        try {
          await db.sleepEntries.bulkAdd(importData.sleepEntries);
        } catch (error) {
          console.warn('Error bulk adding sleep entries during import, trying individual adds:', error);
          // Fallback to individual adds
          for (const entry of importData.sleepEntries) {
            try {
              await db.sleepEntries.add(entry);
            } catch (addError) {
              console.warn('Error adding individual sleep entry during import:', addError);
            }
          }
        }
      }

      if (importData.sleepPatterns && importData.sleepPatterns.length > 0) {
        try {
          await db.sleepPatterns.bulkAdd(importData.sleepPatterns);
        } catch (error) {
          console.warn('Error bulk adding sleep patterns during import, trying individual adds:', error);
          // Fallback to individual adds
          for (const pattern of importData.sleepPatterns) {
            try {
              await db.sleepPatterns.add(pattern);
            } catch (addError) {
              console.warn('Error adding individual sleep pattern during import:', addError);
            }
          }
        }
      }

      if (importData.timezoneHistory && importData.timezoneHistory.length > 0) {
        try {
          await db.timezoneHistory.bulkAdd(importData.timezoneHistory);
        } catch (error) {
          console.warn('Error bulk adding timezone history during import, trying individual adds:', error);
          // Fallback to individual adds
          for (const timezoneEntry of importData.timezoneHistory) {
            try {
              await db.timezoneHistory.add(timezoneEntry);
            } catch (addError) {
              console.warn('Error adding individual timezone entry during import:', addError);
            }
          }
        }
      }
      
      // Create backup after import
      await this.createBackup();
      
      console.log('Data imported successfully');
      return true;
    } catch (error) {
      console.error('Error importing data:', error);
      throw error;
    }
  },

  // Clean up corrupted backup data
  async cleanupBackup() {
    try {
      localStorage.removeItem(STORAGE_KEY);
      console.log('Backup data cleaned up');
      return true;
    } catch (error) {
      console.error('Error cleaning up backup:', error);
      return false;
    }
  },

  // Reset database completely
  async resetDatabase() {
    try {
      // Clear all data
      await db.children.clear();
      await db.sleepEntries.clear();
      await db.sleepPatterns.clear();
      await db.timezoneHistory.clear();
      
      // Clear backup
      await this.cleanupBackup();
      
      console.log('Database reset successfully');
      return true;
    } catch (error) {
      console.error('Error resetting database:', error);
      return false;
    }
  }
};

// Helper functions for database operations
export const sleepDB = {
  // Children operations
  async addChild(childData) {
    try {
      const childToAdd = {
        ...childData,
        createdAt: new Date().toISOString(),
        profileImage: childData.profileImage || null,
        theme: childData.theme || 'default',
        timezone: childData.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
      };
      
      console.log('Adding child to database:', childToAdd);
      const id = await db.children.add(childToAdd);
      console.log('Child added successfully with ID:', id);
      
      await localStorageBackup.createBackup();
      return id;
    } catch (error) {
      console.error('Error adding child:', error);
      throw error;
    }
  },

  async getChildren() {
    try {
      const children = await db.children.toArray();
      return children;
    } catch (error) {
      console.error('Error getting children:', error);
      throw error;
    }
  },

  async updateChild(id, updates) {
    try {
      await db.children.update(id, updates);
      await localStorageBackup.createBackup();
    } catch (error) {
      console.error('Error updating child:', error);
      throw error;
    }
  },

  async deleteChild(id) {
    try {
      // Delete child and all associated data
      await db.sleepEntries.where('childId').equals(id).delete();
      await db.sleepPatterns.where('childId').equals(id).delete();
      await db.timezoneHistory.where('childId').equals(id).delete();
      await db.children.delete(id);
      await localStorageBackup.createBackup();
    } catch (error) {
      console.error('Error deleting child:', error);
      throw error;
    }
  },

  // Sleep entries operations
  async addEntry(entry) {
    try {
      const currentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const id = await db.sleepEntries.add({
        ...entry,
        createdAt: new Date().toISOString(),
        specialEvent: entry.specialEvent || false,
        timezone: entry.timezone || currentTimezone
      });
      await localStorageBackup.createBackup();
      return id;
    } catch (error) {
      console.error('Error adding sleep entry:', error);
      throw error;
    }
  },

  async getEntriesByDate(date, childId) {
    try {
      const startOfDay = new Date(date);
      startOfDay.setHours(0, 0, 0, 0);
      
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);
      
      let query = db.sleepEntries
        .where('date')
        .between(startOfDay.toISOString(), endOfDay.toISOString());
      
      if (childId) {
        query = query.filter(entry => entry.childId === childId);
      }
      
      return await query.toArray();
    } catch (error) {
      console.error('Error getting entries by date:', error);
      throw error;
    }
  },

  async getEntriesByDateRange(startDate, endDate, childId) {
    try {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      
      let query = db.sleepEntries
        .where('date')
        .between(start.toISOString(), end.toISOString());
      
      if (childId) {
        query = query.filter(entry => entry.childId === childId);
      }
      
      return await query.toArray();
    } catch (error) {
      console.error('Error getting entries by date range:', error);
      throw error;
    }
  },

  async updateEntry(id, updates) {
    try {
      await db.sleepEntries.update(id, updates);
      await localStorageBackup.createBackup();
    } catch (error) {
      console.error('Error updating entry:', error);
      throw error;
    }
  },

  async deleteEntry(id) {
    try {
      await db.sleepEntries.delete(id);
      await localStorageBackup.createBackup();
    } catch (error) {
      console.error('Error deleting entry:', error);
      throw error;
    }
  },

  async getAllEntries(childId) {
    try {
      let query = db.sleepEntries.toArray();
      if (childId) {
        query = db.sleepEntries.where('childId').equals(childId).toArray();
      }
      return await query;
    } catch (error) {
      console.error('Error getting all entries:', error);
      throw error;
    }
  },

  // Sleep patterns operations
  async updateSleepPattern(childId, patternType, averageTime, confidence, timezone) {
    try {
      const existing = await db.sleepPatterns
        .where(['childId', 'patternType'])
        .equals([childId, patternType])
        .first();

      if (existing) {
        await db.sleepPatterns.update(existing.id, {
          averageTime,
          confidence,
          timezone: timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          lastUpdated: new Date().toISOString()
        });
      } else {
        await db.sleepPatterns.add({
          childId,
          patternType,
          averageTime,
          confidence,
          timezone: timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          lastUpdated: new Date().toISOString()
        });
      }
      await localStorageBackup.createBackup();
    } catch (error) {
      console.error('Error updating sleep pattern:', error);
      throw error;
    }
  },

  async getSleepPatterns(childId) {
    try {
      return await db.sleepPatterns
        .where('childId')
        .equals(childId)
        .toArray();
    } catch (error) {
      console.error('Error getting sleep patterns:', error);
      throw error;
    }
  },

  // Timezone history operations
  async addTimezoneChange(childId, timezone, changeInfo = null) {
    try {
      await db.timezoneHistory.add({
        childId,
        timezone,
        timestamp: new Date().toISOString(),
        changeInfo: changeInfo ? JSON.stringify(changeInfo) : null
      });
      await localStorageBackup.createBackup();
    } catch (error) {
      console.error('Error adding timezone change:', error);
      throw error;
    }
  },

  async getTimezoneHistory(childId) {
    try {
      return await db.timezoneHistory
        .where('childId')
        .equals(childId)
        .reverse()
        .sortBy('timestamp');
    } catch (error) {
      console.error('Error getting timezone history:', error);
      throw error;
    }
  },

  async getCurrentTimezone(childId) {
    try {
      const child = await db.children.get(childId);
      return child?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (error) {
      console.error('Error getting current timezone:', error);
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
  },

  async updateChildTimezone(childId, newTimezone, changeInfo = null) {
    try {
      // Update child's timezone
      await db.children.update(childId, { timezone: newTimezone });
      
      // Add to timezone history
      await this.addTimezoneChange(childId, newTimezone, changeInfo);
      
      await localStorageBackup.createBackup();
    } catch (error) {
      console.error('Error updating child timezone:', error);
      throw error;
    }
  },

  // Backup operations
  async createBackup() {
    return await localStorageBackup.createBackup();
  },

  async restoreBackup() {
    return await localStorageBackup.restoreBackup();
  },

  async exportData() {
    return await localStorageBackup.exportData();
  },

  async importData(data) {
    return await localStorageBackup.importData(data);
  },

  async resetDatabase() {
    return await localStorageBackup.resetDatabase();
  }
};

// Initialize backup system
export const initializeBackupSystem = async () => {
  try {
    // Test database connection first
    const dbConnected = await db.testConnection();
    if (!dbConnected) {
      console.error('Failed to connect to database');
      return;
    }

    // Try to restore from backup on startup
    const restored = await localStorageBackup.restoreBackup();
    if (restored) {
      console.log('Backup restored successfully on startup');
    } else {
      console.log('No backup found or backup restoration failed, using IndexedDB data');
    }
  } catch (error) {
    console.error('Error during backup restoration on startup:', error);
  }
  
  // Set up periodic backups
  setInterval(async () => {
    try {
      await localStorageBackup.createBackup();
    } catch (error) {
      console.error('Error during periodic backup:', error);
    }
  }, BACKUP_INTERVAL);
  
  // Backup on page unload
  window.addEventListener('beforeunload', async () => {
    try {
      await localStorageBackup.createBackup();
    } catch (error) {
      console.error('Error during page unload backup:', error);
    }
  });
}; 